export type WidgetPosition = "bottom-right" | "bottom-left";
export type WidgetTheme = "light" | "dark" | "auto";

export interface WidgetConfig {
  siteKey: string;
  apiUrl: string;
  position: WidgetPosition;
  theme: WidgetTheme;
  primaryColor: string;
  greeting: string;
  disclaimer: string;
  locale: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  sources?: SourceCitation[];
  createdAt: string;
  /** Server-assigned id for feedback (assistant turns only). */
  serverMessageId?: string;
  feedbackRating?: -1 | 1;
  isGreeting?: boolean;
}

export interface SourceCitation {
  url: string;
  title: string;
  snippet?: string;
}

export interface ChatStreamRequest {
  site_key: string;
  session_id: string;
  message: string;
  source_page?: string;
}

export interface ChatFeedbackRequest {
  site_key: string;
  session_id: string;
  message_id: string;
  rating: -1 | 1;
  comment?: string;
}

export type ChatStreamEventType =
  | "token"
  | "sources"
  | "done"
  | "error"
  | "decline"
  | "handoff"
  | "replace"
  | "meta";

export interface ChatStreamEvent {
  type: ChatStreamEventType;
  content?: string;
  sources?: SourceCitation[];
  error?: string;
  reason?: string;
  message_id?: string;
  intent?: string;
  retrieval_score?: number;
}

export interface WidgetConfigResponse {
  site_key: string;
  greeting: string;
  disclaimer: string;
  quick_actions: QuickAction[];
  theme_defaults: {
    primary_color: string;
    position: WidgetPosition;
  };
}

export interface QuickAction {
  id: string;
  label: string;
  message: string;
}

/** Clearmind brand tokens aligned with iamclearmind.com */
export const CLEARMIND_THEME = {
  light: {
    background: "#ffffff",
    foreground: "#0f1218",
    muted: "#f3f4f6",
    mutedForeground: "#5a6372",
    border: "#0f121814",
    primary: "#16a34a",
    primaryForeground: "#ffffff",
    accent: "#22c55e",
    radius: "12px",
  },
  dark: {
    background: "#171a1e",
    foreground: "#f2f3f5",
    muted: "#1c1f24",
    mutedForeground: "#939dad",
    border: "#ffffff1a",
    primary: "#22c55e",
    primaryForeground: "#08090b",
    accent: "#22c55e",
    radius: "12px",
  },
} as const;

export const DEFAULT_WIDGET_CONFIG: Omit<WidgetConfig, "siteKey" | "apiUrl"> = {
  position: "bottom-right",
  theme: "light",
  primaryColor: CLEARMIND_THEME.light.primary,
  greeting: "Hi! I'm here to help with Clearmind products, fees, and getting started.",
  disclaimer:
    "Educational information only — not investment advice. Past performance is not indicative of future returns.",
  locale: "en-IN",
};
